import React from 'react';
import {
  Globe,
  Clock,
  Stethoscope,
  ClipboardList,
  Users,
  FileText,
  RefreshCw,
  HeartHandshake,
} from 'lucide-react';

const features = [
  {
    icon: Globe,
    title: 'Bilingual / Multilingual Specialist Support',
    description:
      'Consult with doctors who communicate clearly in your preferred language for better comfort and accuracy.',
    accent: 'from-[#1e3a8a] to-[#2563eb]',
  },
  {
    icon: Clock,
    title: 'Time Zone Friendly Scheduling',
    description:
      'Appointments designed around global time zones, not clinic hours.',
    accent: 'from-[#0e7490] to-[#22d3ee]',
  },
  {
    icon: Stethoscope,
    title: 'Specialist Matching by Condition',
    description:
      'Get connected to the right specialist based on your medical concern, not random availability.',
    accent: 'from-[#1e3a8a] to-[#2563eb]',
  },
  {
    icon: ClipboardList,
    title: 'Structured Second Opinion Reviews',
    description:
      'Receive expert-led medical second opinions with organised reports and full clinical context.',
    accent: 'from-[#0e7490] to-[#22d3ee]',
  },
  {
    icon: Users,
    title: 'Collaborative Specialist Oversight',
    description:
      'Complex cases are supported through trusted internal referrals for safer decision-making.',
    accent: 'from-[#1e3a8a] to-[#2563eb]',
  },
  {
    icon: FileText,
    title: 'Pre-Consultation Report Review',
    description:
      'Upload reports in advance so consultations are faster, more focused, and clinically informed.',
    accent: 'from-[#0e7490] to-[#22d3ee]',
  },
  {
    icon: RefreshCw,
    title: 'Follow-Up and Recovery Support',
    description:
      'Care continues beyond the consultation with guided follow-ups and progress tracking.',
    accent: 'from-[#1e3a8a] to-[#2563eb]',
  },
  {
    icon: HeartHandshake,
    title: 'Dedicated Care Coordination',
    description:
      "A guided experience so you don't feel lost between consultations, reports, and next steps.",
    accent: 'from-[#0e7490] to-[#22d3ee]',
  },
];

const FeaturesSection = () => {
  return (
    <section className="relative bg-white py-24 overflow-hidden">
      {/* Subtle background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-[#1e3a8a]/4 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-[#22d3ee]/6 rounded-full blur-3xl" />
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: `linear-gradient(#1e3a8a 1px, transparent 1px), linear-gradient(90deg, #1e3a8a 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0f172a] leading-tight mb-5">
            Healthcare Designed
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#1e3a8a] to-[#22d3ee]">
              Around Real Needs
            </span>
          </h2>

          <p className="max-w-2xl mx-auto text-gray-500 text-lg leading-relaxed">
            Healthcare decisions can feel stressful especially when answers are
            unclear. Globe Medical connects you with verified, competent
            specialists across the globe and supports you through structured
            care journeys designed around real needs.
          </p>

          {/* Decorative divider */}
          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#1e3a8a]/30" />
            <div className="w-2 h-2 rounded-full bg-[#22d3ee]" />
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#1e3a8a]/30" />
          </div>
        </div>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map(({ icon: Icon, title, description, accent }, idx) => (
            <div
              key={idx}
              className="group relative bg-white rounded-2xl border border-gray-100 p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
            >
              {/* Top accent bar */}
              <div
                className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${accent} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
              />

              {/* Icon container */}
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${accent} mb-5 shadow-md group-hover:scale-110 transition-transform duration-300`}>
                <Icon className="w-5 h-5 text-white" strokeWidth={1.8} />
              </div>

              <h3 className="text-[#0f172a] font-semibold text-base leading-snug mb-3 pr-6">
                {title}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                {description}
              </p>

              {/* Bottom hover indicator */}
              <div className={`absolute bottom-0 left-0 w-0 h-[2px] bg-gradient-to-r ${accent} group-hover:w-full transition-all duration-500`} />
            </div>
          ))}
        </div>


      </div>
    </section>
  );
};

export default FeaturesSection;
